import React from 'react';
import PropTypes from 'prop-types';
import { useConfigService } from '@thd-nucleus/experience-context';
import { FULFILLMENT_METHODS } from '../../../constants';
import { Skeleton, SkeletonLine } from '@one-thd/sui-atomic-components';

const DELIVERY_MODE_LABELS = {
  VAN: 'Van',
  FLAT: 'Truck',
  BOX: 'Truck',
  CAR: 'Car',
  PARCEL: 'Car'
};

const getModeSuffix = (type, isBIP, deliveryModeGroup) => {
  if (isBIP || type === FULFILLMENT_METHODS.STH_FFM) {
    return '';
  }
  const modeLabel = DELIVERY_MODE_LABELS[deliveryModeGroup] || '';
  return modeLabel ? ` ${modeLabel} Delivery` : '';
};

const ShippingThreshold = ({
  type = null,
  hasFreeShipping = false,
  totalCharge = null,
  maxCartPriceContributor = false,
  selected = false,
  isCartTiles = false,
  isBIP = false,
  deliveryModeGroup = null,
  sthOptionCount = 0,
  loading = false
}) => {
  let displayDeliveryFeeValue = useConfigService('fs:displayDeliveryFeeValue');
  // Parse the FS value even if it is set as a string number like "15.50" or "15.5"
  displayDeliveryFeeValue = displayDeliveryFeeValue === undefined
    ? null : parseFloat(displayDeliveryFeeValue).toString();

  const showAllDeliveryPrices = !!useConfigService('fs:showAllDeliveryPrices');

  const deliveryFeeAttribute = '$' + (totalCharge || 0);
  const showMultiLineDeliveryFee = isCartTiles && maxCartPriceContributor && selected;
  const getMultiLineDeliveryPrice = () => {
    return <span className="sui-font-bold sui-text-succes" data-delivery-fee={deliveryFeeAttribute}>Included</span>;
  };

  const hasFreeShippingCondtions = [
    hasFreeShipping && type === FULFILLMENT_METHODS.APPLIANCE,
    !totalCharge && [FULFILLMENT_METHODS.STH_FFM, FULFILLMENT_METHODS.EXPRESS_DELIVERY].includes(type)
  ];

  if (hasFreeShippingCondtions.some((condition) => !!condition)) {
    return loading ? <SkeletonLine variant="single" numberOfLines={1} /> : <span className="sui-font-bold sui-text-success" data-delivery-fee={deliveryFeeAttribute}>FREE</span>;
  }

  if ([FULFILLMENT_METHODS.STH_FFM, FULFILLMENT_METHODS.EXPRESS_DELIVERY].includes(type)) {
    if (showMultiLineDeliveryFee) {
      return getMultiLineDeliveryPrice();
    }
  }

  // Show all delivery prices when the FS is enabled
  if (showAllDeliveryPrices && totalCharge) {
    const modeSuffix = getModeSuffix(type, isBIP, deliveryModeGroup);
    const showStartingAt = type === FULFILLMENT_METHODS.STH_FFM && sthOptionCount > 1;
    return loading
      ? <SkeletonLine variant="single" numberOfLines={1} />
      : (
        <span
          className="sui-text-primary"
          data-delivery-fee={deliveryFeeAttribute}
          data-testid="delivery-fee-span"
        >
          {showStartingAt
            ? <><span className="sui-font-normal">Starting at </span><span className="sui-font-bold">{deliveryFeeAttribute}</span></>
            : <><span className="sui-font-bold">{deliveryFeeAttribute}</span>{modeSuffix && <span className="sui-font-normal">{modeSuffix}</span>}</>}
        </span>
      );
  }

  // Display the delivery fee if config value is present and it matches the config value
  const isDisplayDeliveryFeeValue = displayDeliveryFeeValue
    && (displayDeliveryFeeValue === totalCharge?.toString());

  let deliveryFeeElement;
  if (loading) {
    deliveryFeeElement = isDisplayDeliveryFeeValue ? <SkeletonLine variant="single" numberOfLines={1} /> : '';
  } else {
    deliveryFeeElement = isDisplayDeliveryFeeValue ? deliveryFeeAttribute : ' ';
  }

  return (
    <span
      // eslint-disable-next-line @harmony/stencil/no-tailwind-disable
      className={'sui-pt-2 sui-min-h-[24px]' + (isDisplayDeliveryFeeValue ? ' sui-text-primary' : '')}
      data-delivery-fee={deliveryFeeAttribute}
      data-testid="delivery-fee-span"
    >
      { deliveryFeeElement }
    </span>
  );
};

ShippingThreshold.propTypes = {
  type: PropTypes.string,
  hasFreeShipping: PropTypes.bool,
  totalCharge: PropTypes.number,
  maxCartPriceContributor: PropTypes.bool,
  selected: PropTypes.bool,
  isCartTiles: PropTypes.bool,
  isBIP: PropTypes.bool,
  deliveryModeGroup: PropTypes.string,
  sthOptionCount: PropTypes.number,
  loading: PropTypes.bool
};

export { ShippingThreshold };